  import { format, transpose, intervalToDuration } from 'date-fns';
  import { TZDate } from '@date-fns/tz';

export const useDateHelper = () => {
  const userTimezoneOffset = new Date().toString().match(/([-\+][0-9]+)\s/)[1];
  const userTimezoneOffsetFormatted = userTimezoneOffset.substring(0, 3) + ':' + userTimezoneOffset.substring(3) ;  

  // TODO: ADD UNIT TEST
  const timeFromNow = (timestamp) => {
    const now = Date.now();
    const currentFrameTime = new Date(timestamp).getTime();
    const timeDelta = currentFrameTime - now;
    const isPast = timeDelta < 0;

    if (timeDelta === 0) return 'Now';

    const pastDescriptor = isPast
      ? ' <span class="time-offset-descriptor">ago</span>'
      : ''
    const futureDescriptior = !isPast
      ? '<span class="time-offset-descriptor">in</span> '
      : ''

    const intervalStartDate = isPast ? new Date(currentFrameTime) : new Date(now);
    const intervalEndDate = isPast ? new Date(now) : new Date(currentFrameTime);

    const { days, hours, minutes, seconds } = intervalToDuration({
      start: intervalStartDate,
      end: intervalEndDate
    })

    let adjustedMinutes = minutes;
    // If timestamp is in the future, add 1 minute to the minutes value to account for the seconds which are not being displayed if seconds are greater than 30 or minutes is 0.
    // If timestamp is 12:00:00 and current time is 11:59:59, the minutes value will be 0 and seconds will be 1. This will display as "0 mins" which is not accurate, so we add 1 minute to the minutes value.
    if (!isPast) {
      adjustedMinutes = minutes === 0 ? 1 : minutes + (seconds > 30 ? 1 : 0);
    }

    const daysString =
      days > 1
        ? `${days}<span class="time-offset-label">days</span> `
        : days === 1
          ? `${days}<span class="time-offset-label">day</span> `
          : ''

    const hoursString =
      hours > 1
        ? `${hours}<span class="time-offset-label">hrs</span> `
        : hours === 1
          ? `${hours}<span class="time-offset-label">hr</span> `
          : ''

    const minutesString =
      adjustedMinutes === 0 || days > 0 || isNaN(adjustedMinutes)
        ? ''
        : adjustedMinutes === 1
          ? `${adjustedMinutes}<span class="time-offset-label">min</span>`
          : `${adjustedMinutes}<span class="time-offset-label">mins</span>`

    const formattedDistanceToNow =
      (days === 0 || days === undefined) && (hours === 0 || hours === undefined) && (minutes === 0 || minutes === undefined)
      ? 'Now'
      : `${futureDescriptior}${daysString}${hoursString}${minutesString}${pastDescriptor}`

    return formattedDistanceToNow;
  }

  const sentTimeLabel = (timestamp) => {
    if (timestamp == null) return '';

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMs < 60 * 60 * 1000) {
      const mins = Math.max(1, diffMin);
      return `${mins} ${mins === 1 ? 'min' : 'mins'} ago`;
    }
    if (diffMs < 24 * 60 * 60 * 1000) {
      return `${diffHr}${diffHr === 1 ? 'hr' : 'hrs'} ago`;
    }
    if (diffMs < 7 * 24 * 60 * 60 * 1000) {
      return `${diffDay} ${diffDay === 1 ? 'day' : 'days'} ago`;
    }
    if (date.getFullYear() === now.getFullYear()) {
      return format(date, 'LLL d');
    }
    return format(date, 'LLL d, yyyy');
  }

  const observationTimeLabel = (timestamp) => {
    if (timestamp == null) return '';

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const sameDay = date.getFullYear() === now.getFullYear()
      && date.getMonth() === now.getMonth()
      && date.getDate() === now.getDate();
    const sameYear = date.getFullYear() === now.getFullYear();

    const time = format(date, 'h:mm a');
    if (sameDay) return `Today at ${time}`;
    if (sameYear) return `${format(date, 'LLL d')} at ${time}`;
    return `${format(date, 'LLL d, yyyy')} at ${time}`;
  }

  const modelRunTimeFormatted = (options) => {
    const defaultOptions = {
      timestamp: null,
      year: false,
      utcOffset: null,
      timezoneName: null,
      condensed: false
    }

    const { timestamp, year, utcOffset, timezoneName, condensed } = Object.assign({}, defaultOptions, options);

    if (!timestamp) return '';

    const date = new Date(timestamp);
    const dateFormat = year ? 'ccc, LLL d, y' : condensed ? 'LLL d' : 'ccc, LLL d'

   const dateInTimezone = transpose(new TZDate(date, utcOffset), TZDate); 
    const formattedDate = format(dateInTimezone, dateFormat)

    return `${formattedDate}${timezoneName ? ` (${timezoneName})` : ''}`  
  }

  const dateTimeToISOString = (date, utcOffset = userTimezoneOffsetFormatted) => {
    if (!date) return null;

    return date.replace(' ', 'T') + utcOffset
  }

  const datetimeToISOFormat = (date, utcOffset = userTimezoneOffsetFormatted) => {
    if (!date) return null;

    const isoString = dateTimeToISOString(date, utcOffset);

    return new Date(isoString);
  }

  const adjustDateTimeFromUserTZToTZOffset = (date, utcOffset = userTimezoneOffsetFormatted) => {
    date = new Date(date);

    const userDateLocalTime = date.getTime()
    const userLocalTimezoneOffset = date.getTimezoneOffset() * 60000 // in ms
    const dateUTCOffsetMs = utcOffset * 3600000 // in ms
    const offsetDelta = userLocalTimezoneOffset + dateUTCOffsetMs
    const adjustedDateTimeMs = userDateLocalTime + offsetDelta

    return new Date(adjustedDateTimeMs)    
  }

  const adjustDateTimeFromTZOffsetToUserTZ = (date, utcOffset = userTimezoneOffsetFormatted) => {
    date = new Date(date);

    const userDateLocalTime = date.getTime()
    const userLocalTimezoneOffset = date.getTimezoneOffset() * 60000 // in ms
    const dateUTCOffsetMs = utcOffset * 3600000 // in ms
    const offsetDelta = userLocalTimezoneOffset + dateUTCOffsetMs
    const adjustedDateTimeMs = userDateLocalTime - offsetDelta

    return new Date(adjustedDateTimeMs)    
  }

  const convertISODateToUTC = (isoDateString, returnString = false) => {
    if (isoDateString.includes('Z')) {
      return returnString ? isoDateString : new Date(isoDateString).getTime();
    }
    
    const str = isoDateString;
    const match = str.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2}):([0-9]{2})([+-])([0-9]{2}):([0-9]{2})$/);

    if (match) {
      const [ , year, month, day, hour, min, sec, tzSign, tzHour, tzMin ] = match;
      let y = Number(year);
      let m = Number(month) - 1;
      let d = Number(day);
      let h = Number(hour);
      let mi = Number(min);
      let s = Number(sec);
      let tzh = Number(tzHour);
      let tzm = Number(tzMin);

      // Calculate the timezone offset in minutes
      let offset = (tzh * 60 + tzm) * (tzSign === '+' ? -1 : 1);

      // Build the UTC time in ms
      let utcMS = Date.UTC(y, m, d, h, mi, s);
      utcMS += offset * 60 * 1000;

      return returnString ? new Date(utcMS).toISOString() : utcMS
    }
  }

  const formatOffsetToString = (offset) => {
    const sign = offset >= 0 ? '+' : '-'
    const abs = Math.abs(offset)
    const hours = String(Math.floor(abs)).padStart(2, '0')

    return `${sign}${hours}00`
  }

  return {
    userTimezoneOffset,
    userTimezoneOffsetFormatted,
    adjustDateTimeFromUserTZToTZOffset,
    adjustDateTimeFromTZOffsetToUserTZ,
    convertISODateToUTC,
    datetimeToISOFormat,
    dateTimeToISOString,
    formatOffsetToString,
    modelRunTimeFormatted,
    observationTimeLabel,
    sentTimeLabel,
    timeFromNow,
  }
}
